import { Box, Button, Divider, TextField, Typography } from "@mui/material";
import "./CreateJobForm.css";
import AutocompleteBox from "../AutocompleteBox";
import { useForm } from "react-hook-form";
import { createJobSchema, CreateJobSchemaType } from "./CreateJobScheme";
import { yupResolver } from "@hookform/resolvers/yup";
import {
  useCreatedJobData,
  useMasterData,
} from "../../context/MasterContextProvider";
import { Skill } from "../../models/Job";
import { createJobApi, editJob } from "../../api/job";
import { getApplicantStatusConfig } from "../../api/applicants";
import { useSnackbar } from "../../context/SnackbarContext";
import { useState, useRef, useEffect } from "react";
import RichText from "../RichText/RichText";
import { useRegion } from "../../context/RegionContext";
import { REGIONS, Region } from "../../constants/regions";
import { getCitiesApi } from "../../api/master";
import { CityItem } from "../../models/master";
import { getCountryIdByRegion } from "../../utils/countryCache";

interface Props {
  defaultValue: CreateJobSchemaType;
  handleDone: any;
  type?: string;
  jobId?: string;
}

// Saudi Arabia cities (main regions/provinces)

const JobForm = ({ defaultValue, handleDone, type, jobId }: Props) => {
  console.log("defaultValue", defaultValue);
  const { region } = useRegion();
  const countryName = JSON.parse(localStorage.getItem("region") || "{}")?.countryName?.toLowerCase()
  // Get region from localStorage as fallback, handle both JSON and plain string formats
  const getRegionFromStorage = (): Region => {
    if (typeof window !== "undefined") {
      try {
        const storedRegion = localStorage.getItem("region");
        if (storedRegion) {
          let countryName: string | undefined;
          // Try to parse as JSON first
          try {
            const parsed = JSON.parse(storedRegion);
            countryName = parsed?.countryName;
          } catch {
            // If parsing fails, it's likely a plain string, use it directly
            countryName = storedRegion;
          }
          const normalizedRegion = countryName?.toLowerCase();
          if (
            normalizedRegion === REGIONS.SA ||
            normalizedRegion === REGIONS.AE
          ) {
            return normalizedRegion as Region;
          }
        }
      } catch (error) {
        console.error("Error reading region from localStorage:", error);
      }
    }
    return region;
  };

  const currentRegion = getRegionFromStorage();

  // Only override currency defaults for "Create Job" flows.
  // Edit/Clone should keep whatever currency comes from the existing job.
  const isCreateMode = !type || type === "create";
  const didUserChangeCurrencyRef = useRef(false);
  const lastCurrencyRegionRef = useRef<Region | null>(null);

  const { register, formState, handleSubmit, setValue, reset, watch } =
    useForm<CreateJobSchemaType>({
      mode: "onTouched",
      resolver: yupResolver(createJobSchema),
      defaultValues: {
        ...defaultValue,
        // For SA, if workState exists, use it as province
        province:
          currentRegion === REGIONS.SA && defaultValue.workState
            ? defaultValue.workState
            : defaultValue.province || "",
        city: defaultValue.city || "",
        // Always set country based on effective region, overriding any defaultValue
        countryName:
          currentRegion === REGIONS.SA
            ? "Saudi Arabia"
            : "United Arab Emirates",
      },
    });

  const [loading, setIsLoading] = useState(false);
  const [cities, setCities] = useState<CityItem[]>([]);

  // Custom dropdown states
  const [isJobLevelOpen, setIsJobLevelOpen] = useState(false);
  const [isCountryOpen, setIsCountryOpen] = useState(false);
  const [isEmirateOpen, setIsEmirateOpen] = useState(false);
  const [isProvinceOpen, setIsProvinceOpen] = useState(false);
  const [isAreaOpen, setIsAreaOpen] = useState(false);
  const [isJobTypeOpen, setIsJobTypeOpen] = useState(false);
  const [isIndustryOpen, setIsIndustryOpen] = useState(false);
  const [isWorkTypeOpen, setIsWorkTypeOpen] = useState(false);
  const [isEducationOpen, setIsEducationOpen] = useState(false);
  const [isCurrencyOpen, setIsCurrencyOpen] = useState(false);

  // Dropdown refs
  const jobLevelDropdownRef = useRef<HTMLDivElement>(null);
  const countryDropdownRef = useRef<HTMLDivElement>(null);
  const emirateDropdownRef = useRef<HTMLDivElement>(null);
  const provinceDropdownRef = useRef<HTMLDivElement>(null);
  const areaDropdownRef = useRef<HTMLDivElement>(null);
  const jobTypeDropdownRef = useRef<HTMLDivElement>(null);
  const industryDropdownRef = useRef<HTMLDivElement>(null);
  const workTypeDropdownRef = useRef<HTMLDivElement>(null);
  const educationDropdownRef = useRef<HTMLDivElement>(null);
  const currencyDropdownRef = useRef<HTMLDivElement>(null);

  // Refs for scrolling to error fields
  const formRefs = {
    jobName: useRef<HTMLDivElement>(null),
    jobLevel: useRef<HTMLDivElement>(null),
    workState: useRef<HTMLDivElement>(null),
    province: useRef<HTMLDivElement>(null),
    city: useRef<HTMLDivElement>(null),
    minExp: useRef<HTMLDivElement>(null),
    maxExp: useRef<HTMLDivElement>(null),
    jobType: useRef<HTMLDivElement>(null),
    workType: useRef<HTMLDivElement>(null),
    qualTypeName: useRef<HTMLDivElement>(null),
    jobDesc: useRef<HTMLDivElement>(null),
    skills: useRef<HTMLDivElement>(null),
  };

  // Always sync country with effective region
  useEffect(() => {
    const storedRegion =
      typeof window !== "undefined"
        ? JSON.parse(
            localStorage.getItem("region") || "{}"
          )?.countryName?.toLowerCase()
        : null;
    const effectiveRegion =
      storedRegion === REGIONS.SA || storedRegion === REGIONS.AE
        ? storedRegion
        : currentRegion;

    const defaultCountry =
      effectiveRegion === REGIONS.SA ? "Saudi Arabia" : "United Arab Emirates";

    // Always set the country to match the effective region
    setValue("countryName", defaultCountry, { shouldValidate: false });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [setValue, currentRegion]);

  // Scroll to first error field
  useEffect(() => {
    if (Object.keys(formState.errors).length > 0) {
      const firstErrorField = Object.keys(
        formState.errors
      )[0] as keyof typeof formRefs;
      if (formRefs[firstErrorField]?.current) {
        formRefs[firstErrorField].current.scrollIntoView({
          behavior: "smooth",
          block: "center",
        });
      }
    }
  }, [formState.errors]);

  // Click outside handlers for custom dropdowns
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        jobLevelDropdownRef.current &&
        !jobLevelDropdownRef.current.contains(event.target as Node)
      ) {
        setIsJobLevelOpen(false);
      }
      if (
        countryDropdownRef.current &&
        !countryDropdownRef.current.contains(event.target as Node)
      ) {
        setIsCountryOpen(false);
      }
      if (
        emirateDropdownRef.current &&
        !emirateDropdownRef.current.contains(event.target as Node)
      ) {
        setIsEmirateOpen(false);
      }
      if (
        provinceDropdownRef.current &&
        !provinceDropdownRef.current.contains(event.target as Node)
      ) {
        setIsProvinceOpen(false);
      }
      if (
        areaDropdownRef.current &&
        !areaDropdownRef.current.contains(event.target as Node)
      ) {
        setIsAreaOpen(false);
      }
      if (
        jobTypeDropdownRef.current &&
        !jobTypeDropdownRef.current.contains(event.target as Node)
      ) {
        setIsJobTypeOpen(false);
      }
      if (
        industryDropdownRef.current &&
        !industryDropdownRef.current.contains(event.target as Node)
      ) {
        setIsIndustryOpen(false);
      }
      if (
        workTypeDropdownRef.current &&
        !workTypeDropdownRef.current.contains(event.target as Node)
      ) {
        setIsWorkTypeOpen(false);
      }
      if (
        educationDropdownRef.current &&
        !educationDropdownRef.current.contains(event.target as Node)
      ) {
        setIsEducationOpen(false);
      }
      if (
        currencyDropdownRef.current &&
        !currencyDropdownRef.current.contains(event.target as Node)
      ) {
        setIsCurrencyOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const {
    industries,
    jobLevels,
    currencies,
    countries,
    states,
    workTypes,
    workPlaces,
    qualificationTypes,
  } = useMasterData();

  // For Create Job, ensure currency matches the active region (UAE->AED, KSA->SAR).
  // This fixes cases where parent components hard-code currency defaults.
  useEffect(() => {
    if (!isCreateMode) return;
    if (!currencies || currencies.length === 0) return;
    if (didUserChangeCurrencyRef.current) return;
    if (lastCurrencyRegionRef.current === currentRegion) return;

    const targetCurrencyCode = currentRegion === REGIONS.SA ? "SAR" : "AED";
    const targetCurrency =
      currencies.find(
        (c) => c.currencyName?.toLowerCase() === targetCurrencyCode.toLowerCase()
      ) ?? null;

    if (!targetCurrency) return;

    setValue("currencyId", targetCurrency.currencyId, {
      shouldValidate: true,
      shouldDirty: true,
    });
    setValue("currencyName", targetCurrency.currencyName, {
      shouldValidate: false,
      shouldDirty: true,
    });

    lastCurrencyRegionRef.current = currentRegion;
  }, [currencies, currentRegion, isCreateMode, setValue]);

  // Watch province and city values
  const currentProvince = watch("province");
  const currentCity = watch("city");

  const selectedQualification =
    qualificationTypes?.find((q) => q._id === String(watch("qualTypeId"))) ||
    qualificationTypes?.find((q) => q.qualTypeName === watch("qualTypeName"));

  function getStateIdByName(name: string): string {
    const state = states?.find((state) => state.stateName === name);
    return state ? state.id.toString() : "";
  }

  function getCityIdByName(name: string): string {
    const city = cities?.find((city) => city.cityName === name);
    return city ? city.id : "";
  }

  function getCityNameById(cityId: string): string {
    const city = cities?.find((city) => city.id === cityId);
    return city ? city.cityName : "";
  }

  // Helper function to get cities for a province
  const getCitiesForProvince = (provinceName: string | undefined): string[] => {
    if (!provinceName || !cities.length) return [];
    // Filter cities that belong to the selected province/state
    return cities
      .filter((city) => {
        // Find the state that matches the province name
        const state = states?.find((state) => state.stateName === provinceName);
        return state && city.stateId === state.id;
      })
      .map((city) => city.cityName);
  };

  // Get current cities based on selected province
  const currentProvinceCities = getCitiesForProvince(currentProvince);

  // Fetch cities when province is selected for Saudi Arabia
  useEffect(() => {
    const fetchCities = async () => {
      const storedRegion =
        typeof window !== "undefined"
          ? JSON.parse(localStorage.getItem("region") || "{}")?.countryName?.toLowerCase()
          : null;
      const effectiveRegion =
        storedRegion === REGIONS.SA || storedRegion === REGIONS.AE
          ? storedRegion
          : currentRegion;

      if (effectiveRegion === REGIONS.SA && currentProvince) {
        const stateId = getStateIdByName(currentProvince);
        if (stateId) {
          try {
            const response = await getCitiesApi(stateId);
            if (response?.data) {
              setCities(response.data);
            }
          } catch (error) {
            console.error("Error fetching cities:", error);
            setCities([]);
          }
        } else {
          setCities([]);
        }
      } else {
        // Clear cities when not in Saudi Arabia or no province selected
        setCities([]);
      }
    };

    fetchCities();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentProvince, currentRegion, states]);

  // Resolve city name from cityId when cities are loaded (for edit mode)
  useEffect(() => {
    // Only resolve if we have cityId but no city name (or empty string), and cities are loaded
    if (
      defaultValue.cityId &&
      (!currentCity || currentCity === "") &&
      cities.length > 0 &&
      currentProvince
    ) {
      const cityName = getCityNameById(defaultValue.cityId);
      if (cityName) {
        // Verify the city belongs to the current province before setting
        const validCities = getCitiesForProvince(currentProvince);
        if (validCities.includes(cityName)) {
          setValue("city", cityName, { shouldValidate: false });
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cities, defaultValue.cityId, currentProvince]);

  // Clear city if it's not valid for the current province
  useEffect(() => {
    if (currentProvince && currentCity) {
      const validCities = getCitiesForProvince(currentProvince);
      if (!validCities.includes(currentCity)) {
        setValue("city", "", { shouldValidate: false });
      }
    } else if (!currentProvince && currentCity) {
      // Clear city if province is cleared
      setValue("city", "", { shouldValidate: false });
    }
  }, [currentProvince, currentCity, setValue, cities, states]);

  const { showSnackbar } = useSnackbar();

  const { setCreatedJob } = useCreatedJobData();

  const [jobNameError, setJobNameError] = useState("");

  const handleFormSubmit = async (data: CreateJobSchemaType) => {
    setIsLoading(true);

    const newData = { ...data };

    // Determine country from form data
    const selectedCountry =
      data.countryName ||
      (region === REGIONS.SA ? "Saudi Arabia" : "United Arab Emirates");
    // Set countryId from region mapping (same as used in headers), then fallback by country name.
    const normalizedRegion = currentRegion?.toLowerCase() || REGIONS.SA;
    newData.countryId =
      getCountryIdByRegion(normalizedRegion) ||
      getCountryIdByRegion(REGIONS.SA) ||
      "";
    if (!newData.countryId) {
      const selectedCountryItem = countries?.find(
        (country) => country.countryName === selectedCountry
      );
      newData.countryId = selectedCountryItem?.id || "";
    }
    newData.workStateId = getStateIdByName(data.workState);
    newData.stateId = getStateIdByName(data.workState);
    // For Saudi Arabia, use province as workState, otherwise use workState
    if (selectedCountry === "Saudi Arabia") {

      const cityValue = watch("city") || data.city;
  
      if (cityValue) {
        newData.cityId = getCityIdByName(cityValue);
      }
    } 
    // else {
    //   const resolvedWorkStateId = getStateIdByName(data.workState);
    //   // If master data hasn't loaded yet, keep any prefilled value.
    //   newData.workStateId =
    //     resolvedWorkStateId || (data.workStateId as string) || "";
    //   newData.countryName = "United Arab Emirates";
    //   // Set stateId when workState is selected
    //   if (data.workState) {
    //     const resolvedStateId = getStateIdByName(data.workState);
    //     if (resolvedStateId) newData.stateId = resolvedStateId;
    //   }
    // }
    // IDs are captured during dropdown selection; submit should pass them as-is.
    // workTypeId => selected Job Type id
    newData.workTypeId =
      (data.workTypeId as string) ||
      workTypes?.find(
        (workType) =>
          String(workType.workTypeName || "")
            .toLowerCase()
            .replace(/[\s-]/g, "") ===
          String(data.jobType || "")
            .toLowerCase()
            .replace(/[\s-]/g, "")
      )?._id ||
      "";
    // workplaceTypeId => selected Work Place Type id
    (newData as any).workplaceTypeId = (data as any).workplaceTypeId || "";
    newData.jobLevelId = (data.jobLevelId as string) || "";
    newData.industryId = (data.industryId as string) || "";
    newData.currencyId = (data.currencyId as string) || "";
    const normalizeLabel = (value: any): string =>
      String(value || "")
        .toLowerCase()
        // remove spaces and common punctuation for safer matching
        .replace(/[\s\-\/'’&.,]/g, "");

    const qualTypeNameNorm = normalizeLabel(data.qualTypeName);

    const matchedQualification =
      qualificationTypes?.find((qualification) => {
        const qualificationNorm = normalizeLabel(qualification.qualTypeName);
        return (
          qualificationNorm === qualTypeNameNorm ||
          qualificationNorm.includes(qualTypeNameNorm) ||
          qualTypeNameNorm.includes(qualificationNorm)
        );
      })?._id || "";

    newData.qualTypeId = (data.qualTypeId as string) || matchedQualification;
    // Backward compatibility for legacy/default numeric currencyId values.
    if (newData.currencyId && !String(newData.currencyId).match(/^[a-f0-9]{24}$/i)) {
      const selectedCurrency = currencies?.find(
        (currency) => String(currency.currencyId) === String(newData.currencyId)
      );
      newData.currencyId = selectedCurrency?._id || "";
    }

    // Clean jobDesc - check if it's actually empty content from ReactQuill
    const isJobDescEmpty =
      !jobDesc ||
      jobDesc === "<p><br></p>" ||
      jobDesc === "<p></p>" ||
      jobDesc.replace(/<[^>]*>/g, "").trim() === "";

    newData.jobDesc = isJobDescEmpty ? "" : jobDesc;
    newData.jobReq = jobReq;

    if (newData.maxExp) {
      if (!newData.minExp) {
        newData.minExp = 0;
      }
    }

    if (type === "edit") {
      const resp = await editJob(newData, jobId || "");
      if (resp?.responseInfo.httpCode === 200) {
        setIsLoading(false);

        reset();
        handleDone();

        // Show success message for edit
        showSnackbar(`${data.jobName} updated successfully`, "success");
      } else {
        setIsLoading(false);

        // Check if it's a duplicate job name error for edit case too
        if (
          resp?.responseInfo?.description?.includes("job with the same name") ||
          resp?.responseInfo?.description?.includes(
            "active job with the same name"
          )
        ) {
          setJobNameError(resp?.responseInfo?.description);
          // Scroll to job name field
          if (formRefs.jobName?.current) {
            formRefs.jobName.current.scrollIntoView({
              behavior: "smooth",
              block: "center",
            });
          }
        } else {
          showSnackbar(
            resp?.responseInfo?.description || "Something went wrong.",
            "error"
          );
        }
      }
    } else {
      let resp;

      // If cloning, get the funnel config first
      if (type === "clone" && jobId) {
        try {
          const funnelConfig = await getApplicantStatusConfig(jobId);
          const statusConfig = funnelConfig?.data?.statusConfig;
          resp = await createJobApi(newData, statusConfig);
         
        } catch (error) {
          console.error("Error getting funnel config for clone:", error);
          resp = await createJobApi(newData);
        }
      } else {
        resp = await createJobApi(newData);
      }

      if (resp?.responseInfo.httpCode === 200) {
        setIsLoading(false);
        reset();

        handleDone();
        setCreatedJob((prevState) => prevState + 1);

        // Show appropriate success message
        const message =
          type === "clone"
            ? `${data.jobName} created successfully`
            : "Job created successfully";
        showSnackbar(message, "success");
      } else {
        setIsLoading(false);

        // Check if it's a duplicate job name error
        if (
          resp?.responseInfo?.description?.includes("job with the same name") ||
          resp?.responseInfo?.description?.includes(
            "active job with the same name"
          )
        ) {
          setJobNameError(resp?.responseInfo?.description);
          // Scroll to job name field
          if (formRefs.jobName?.current) {
            formRefs.jobName.current.scrollIntoView({
              behavior: "smooth",
              block: "center",
            });
          }
        } else {
          showSnackbar(
            resp?.responseInfo?.description || "Something went wrong.",
            "error"
          );
        }
      }
    }
  };

  const handleSkillChange = (newSkills: Skill[]) => {
    setValue("skills", newSkills, { shouldDirty: true, shouldValidate: true });
  };

  const skills = watch("skills");

  const [jobDesc, setJobDesc] = useState(defaultValue.jobDesc || "");
  const [jobReq, setJobReq] = useState(defaultValue.jobReq || "");

  const handleJobDesc = (value: string) => {
    setJobDesc(value);

    // Check if RichText content is actually empty (ReactQuill returns <p><br></p> for empty)
    const isEmpty =
      !value ||
      value === "<p><br></p>" ||
      value === "<p></p>" ||
      value.replace(/<[^>]*>/g, "").trim() === "";

    const cleanValue = isEmpty ? "" : value;
    setValue("jobDesc", cleanValue, {
      shouldValidate: true,
      shouldTouch: true,
    });
  };

  // Helper to get current country with fallback to region-based default
  const getCurrentCountry = (): string => {
    const storedRegion =
      typeof window !== "undefined"
        ? JSON.parse(localStorage.getItem("region") || "{}")?.countryName?.toLowerCase()
        : null;
    const effectiveRegion =
      storedRegion === REGIONS.SA || storedRegion === REGIONS.AE
        ? storedRegion
        : currentRegion;
console.log("storedRegion",storedRegion);
console.log("currentRegion",currentRegion);


    return (
      watch("countryName") ||
      (effectiveRegion === REGIONS.SA ? "Saudi Arabia" : "United Arab Emirates")
    );
  };

  return (
    <>
      <form
        style={{
          display: "flex",
          flexDirection: "column",
          justifyContent: "space-between",
        }}
        className="create_job_form_wrapper"
        onSubmit={handleSubmit(handleFormSubmit)}
      >
        <h3>Job Details</h3>
        <div className="school-img-box">
          <div className="school-form-inner-box" ref={formRefs.jobName}>
            <Typography
              variant="body1"
              component="p"
              sx={{ fontSize: "14px", fontWeight: "bold", mb: 1 }}
            >
              Job Title
              <span style={{ color: "red" }}>*</span>
            </Typography>
            <TextField
              placeholder="Job Title"
              {...register("jobName")}
              error={!!formState.errors.jobName || !!jobNameError}
              helperText={formState.errors.jobName?.message || jobNameError}
              size="small"
              type="text"
              onChange={(e) => {
                register("jobName").onChange(e);
                if (jobNameError) setJobNameError("");
              }}
            />
          </div>
        </div>
        <div className="school-img-box">
          <div className="school-form-inner-box">
            <Typography
              variant="body1"
              component="p"
              sx={{ fontSize: "14px", fontWeight: "bold", mb: 1 }}
            >
              Salary    
              <span className="secondaryGrayText"> (optional)</span>
            </Typography>
            <div style={{ display: "flex", gap: "4px", alignItems: "center" }}>
              <TextField
                placeholder="Min."
                {...register("minSalary")}
                // error={!!formState.errors.minSalary}
                // helperText={formState.errors.minSalary?.message}
                fullWidth
                size="small"
                type="number"
                inputProps={{
                  min: 0,
                  step: 1,
                }}
                onKeyDown={(e) => {
                  if (
                    e.key === "e" ||
                    e.key === "E" ||
                    e.key === "-" ||
                    e.key === "+"
                  ) {
                    e.preventDefault();
                  }
                }}
                onChange={(e) => {
                  const value = Math.max(0, parseInt(e.target.value) || 0);
                  setValue("minSalary", value, { shouldValidate: true });
                }}
              />
              <TextField
                placeholder="Max."
                {...register("maxSalary")}
                fullWidth
                size="small"
                type="number"
                inputProps={{
                  min: 0,
                  step: 1,
                }}
                onKeyDown={(e) => {
                  if (
                    e.key === "e" ||
                    e.key === "E" ||
                    e.key === "-" ||
                    e.key === "+"
                  ) {
                    e.preventDefault();
                  }
                }}
                onChange={(e) => {
                  const value = Math.max(0, parseInt(e.target.value) || 0);
                  setValue("maxSalary", value, { shouldValidate: true });
                }}
              />
              <div
                className="custom-dropdown-container"
                ref={currencyDropdownRef}
                style={{ maxWidth: "100px", minWidth: "80px" }}
              >
                <div
                  className={`custom-dropdown ${
                    isCurrencyOpen ? "active" : ""
                  } ${!!formState.errors.currencyId ? "error" : ""}`}
                  onClick={() => setIsCurrencyOpen(!isCurrencyOpen)}
                  style={{ minWidth: "80px" }}
                >
                  <span
                    className={`custom-dropdown-text ${
                      !watch("currencyId") ? "custom-dropdown-placeholder" : ""
                    }`}
                  >
                    {currencies?.find(
                      (c) =>
                        c._id === String(watch("currencyId")) ||
                        c.currencyId === Number(watch("currencyId"))
                    )?.currencyName || "Currency"}
                  </span>
                  <svg
                    className={`custom-dropdown-arrow ${
                      isCurrencyOpen ? "open" : ""
                    }`}
                    width="20"
                    height="20"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                  >
                    <path
                      fillRule="evenodd"
                      d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                      clipRule="evenodd"
                    />
                  </svg>
                </div>

                {isCurrencyOpen && (
                  <div className="custom-dropdown-menu">
                    <div className="custom-dropdown-options">
                      {currencies?.map((currency, idx) => (
                        <div
                          key={idx}
                          className={`custom-dropdown-option ${
                            watch("currencyId") === currency.currencyId
                              ? "selected"
                              : ""
                          }`}
                          onClick={() => {
                            didUserChangeCurrencyRef.current = true;
                            setValue("currencyId", currency._id, {
                              shouldValidate: true,
                              shouldDirty: true,
                            });
                            setValue("currencyName", currency.currencyName, {
                              shouldValidate: false,
                              shouldDirty: true,
                            });
                            setIsCurrencyOpen(false);
                          }}
                        >
                          {currency.currencyName}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
          <div className="school-form-inner-box" ref={formRefs.jobLevel}>
            {" "}
            <Typography
              variant="body1"
              component="p"
              sx={{ fontSize: "14px", fontWeight: "bold", mb: 1 }}
            >
              Job Level
              <span style={{ color: "red" }}>*</span>
            </Typography>
            <div
              className="custom-dropdown-container"
              ref={jobLevelDropdownRef}
            >
              <div
                className={`custom-dropdown ${isJobLevelOpen ? "active" : ""} ${
                  !!formState.errors.jobLevel ? "error" : ""
                }`}
                onClick={() => setIsJobLevelOpen(!isJobLevelOpen)}
              >
                <span
                  className={`custom-dropdown-text ${
                    !watch("jobLevel") ? "custom-dropdown-placeholder" : ""
                  }`}
                >
                  {watch("jobLevel") || "Select Job Level"}
                </span>
                <svg
                  className={`custom-dropdown-arrow ${
                    isJobLevelOpen ? "open" : ""
                  }`}
                  width="20"
                  height="20"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                >
                  <path
                    fillRule="evenodd"
                    d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                    clipRule="evenodd"
                  />
                </svg>
              </div>

              {isJobLevelOpen && (
                <div className="custom-dropdown-menu">
                  <div className="custom-dropdown-options">
                    {jobLevels?.map((level, idx) => (
                      <div
                        key={idx}
                        className={`custom-dropdown-option ${
                          watch("jobLevel") === level.jobLevelName
                            ? "selected"
                            : ""
                        }`}
                        onClick={() => {
                          setValue("jobLevel", level.jobLevelName, {
                            shouldValidate: true,
                          });
                          setValue("jobLevelId", level._id, {
                            shouldValidate: false,
                          });
                          setIsJobLevelOpen(false);
                        }}
                      >
                        {level.jobLevelName}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
            {!!formState.errors.jobLevel && (
              <span className="errorMessage">
                {formState.errors.jobLevel.message}
              </span>
            )}
          </div>
        </div>

        {/* Country, Province/Emirate, City in single row */}
        <div
          className="school-img-box"
          style={{ flexWrap: "nowrap", gap: "12px" }}
        >
          {/* Country Selector */}
          <div
            className="school-form-inner-box"
            style={{ flex: "0 1 60%", minWidth: "0", maxWidth: "100%" }}
          >
            <Typography
              variant="body1"
              component="p"
              sx={{ fontSize: "14px", fontWeight: "bold", mb: 1 }}
            >
              Country
              <span style={{ color: "red" }}>*</span>
            </Typography>
            <div className="custom-dropdown-container" ref={countryDropdownRef}>
              <div
                className={`custom-dropdown ${isCountryOpen ? "active" : ""} ${
                  !!formState.errors.countryName ? "error" : ""
                }`}
                onClick={() => setIsCountryOpen(!isCountryOpen)}
                style={{ minWidth: "auto" }}
              >
                <span className="custom-dropdown-text">
                  {getCurrentCountry() === "Saudi Arabia" ? (
                    <>
                      <span style={{ marginRight: "8px" }}>🇸🇦</span>
                      Saudi Arabia
                    </>
                  ) : (
                    <>
                      <span style={{ marginRight: "8px" }}>🇦🇪</span>
                      United Arab Emirates
                    </>
                  )}
                </span>
                <svg
                  className={`custom-dropdown-arrow ${
                    isCountryOpen ? "open" : ""
                  }`}
                  width="20"
                  height="20"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                >
                  <path
                    fillRule="evenodd"
                    d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                    clipRule="evenodd"
                  />
                </svg>
              </div>

              {isCountryOpen && (
                <div className="custom-dropdown-menu">
                  <div className="custom-dropdown-options">
                    {(() => {
                      const storedRegion =
                        typeof window !== "undefined"
                          ? countryName?.toLowerCase()
                          : null;
                      const effectiveRegion =
                        storedRegion === REGIONS.SA ||
                        storedRegion === REGIONS.AE
                          ? storedRegion
                          : currentRegion;

                      // Show only the option that matches the region
                      if (effectiveRegion === REGIONS.SA) {
                        return (
                          <div
                            className="custom-dropdown-option selected"
                            onClick={() => {
                              setValue("countryName", "Saudi Arabia", {
                                shouldValidate: true,
                              });
                              setIsCountryOpen(false);
                            }}
                          >
                            <span style={{ marginRight: "8px" }}>🇸🇦</span>
                            Saudi Arabia
                          </div>
                        );
                      } else {
                        return (
                          <div
                            className="custom-dropdown-option selected"
                            onClick={() => {
                              setValue("countryName", "United Arab Emirates", {
                                shouldValidate: true,
                              });
                              setIsCountryOpen(false);
                            }}
                          >
                            <span style={{ marginRight: "8px" }}>🇦🇪</span>
                            United Arab Emirates
                          </div>
                        );
                      }
                    })()}
                  </div>
                </div>
              )}
            </div>
            <input type="hidden" {...register("countryName")} />
          </div>

          {/* Conditional fields based on selected country */}
          {getCurrentCountry() === "Saudi Arabia" ? (
            <>
              {/* Province for Saudi Arabia */}
              <div
                className="school-form-inner-box"
                ref={formRefs.province}
                style={{ flex: "1 1 60%", minWidth: "0", maxWidth: "100%" }}
              >
                <Typography
                  variant="body1"
                  component="p"
                  sx={{ fontSize: "14px", fontWeight: "bold", mb: 1 }}
                >
                  Province
                  <span style={{ color: "red" }}>*</span>
                </Typography>
                <div
                  className="custom-dropdown-container"
                  ref={provinceDropdownRef}
                >
                  <div
                    className={`custom-dropdown ${
                      isProvinceOpen ? "active" : ""
                    } ${!!formState.errors.workState ? "error" : ""}`}
                    onClick={() => setIsProvinceOpen(!isProvinceOpen)}
                    style={{ minWidth: "auto" }}
                  >
                    <span
                      className={`custom-dropdown-text ${
                        !watch("province") ? "custom-dropdown-placeholder" : ""
                      }`}
                    >
                      {watch("province") || "Select Province"}
                    </span>
                    <svg
                      className={`custom-dropdown-arrow ${
                        isProvinceOpen ? "open" : ""
                      }`}
                      width="20"
                      height="20"
                      viewBox="0 0 20 20"
                      fill="currentColor"
                    >
                      <path
                        fillRule="evenodd"
                        d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                        clipRule="evenodd"
                      />
                    </svg>
                  </div>

                  {isProvinceOpen && (
                    <div className="custom-dropdown-menu">
                      <div className="custom-dropdown-options">
                        {states?.map((state, idx) => (
                          <div
                            key={idx}
                            className={`custom-dropdown-option ${
                              watch("province") === state.stateName
                                ? "selected"
                                : ""
                            }`}
                            onClick={async () => {
                              setValue("province", state.stateName, {
                                shouldValidate: true,
                              });
                              setValue("workState", state.stateName, {
                                shouldValidate: true,
                              });
                              setValue("city", "", {
                                shouldValidate: false,
                              });
                              setIsProvinceOpen(false);

                              // Fetch cities for the selected state
                              const stateId = getStateIdByName(state.stateName);
                              if (stateId) {
                                try {
                                  const response = await getCitiesApi(stateId);
                                  if (response?.data) {
                                    setCities(response.data);
                                  }
                                } catch (error) {
                                  console.error(
                                    "Error fetching cities:",
                                    error
                                  );
                                  setCities([]);
                                }
                              } else {
                                setCities([]);
                              }
                            }}
                          >
                            {state.stateName}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
                {!!formState.errors.workState && (
                  <span className="errorMessage">
                    {formState.errors.workState.message}
                  </span>
                )}
                <input type="hidden" {...register("province")} />
              </div>
              {/* City for Saudi Arabia */}
              <div
                className="school-form-inner-box"
                ref={formRefs.city}
                style={{ flex: "1 1 60%", minWidth: "0", maxWidth: "100%" }}
              >
                <Typography
                  variant="body1"
                  component="p"
                  sx={{ fontSize: "14px", fontWeight: "bold", mb: 1 }}
                >
                  City
                  <span className="secondaryGrayText"> (Optional)</span>
                </Typography>
                <div
                  className="custom-dropdown-container"
                  ref={areaDropdownRef}
                >
                  <div
                    className={`custom-dropdown ${isAreaOpen ? "active" : ""} ${
                      !watch("province") ? "disabled" : ""
                    }`}
                    onClick={() => {
                      if (watch("province")) {
                        setIsAreaOpen(!isAreaOpen);
                      }
                    }}
                    style={{
                      opacity: !watch("province") ? 0.6 : 1,
                      cursor: !watch("province") ? "not-allowed" : "pointer",
                      minWidth: "auto",
                    }}
                  >
                    <span
                      className={`custom-dropdown-text ${
                        !watch("city") ? "custom-dropdown-placeholder" : ""
                      }`}
                    >
                      {watch("city") || "Select City"}
                    </span>
                    <svg
                      className={`custom-dropdown-arrow ${
                        isAreaOpen ? "open" : ""
                      }`}
                      width="20"
                      height="20"
                      viewBox="0 0 20 20"
                      fill="currentColor"
                    >
                      <path
                        fillRule="evenodd"
                        d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                        clipRule="evenodd"
                      />
                    </svg>
                  </div>

                  {isAreaOpen && watch("province") && (
                    <div className="custom-dropdown-menu">
                      <div className="custom-dropdown-options">
                        {currentProvinceCities.length > 0 ? (
                          currentProvinceCities.map((cityName, idx) => (
                            <div
                              key={idx}
                              className={`custom-dropdown-option ${
                                watch("city") === cityName ? "selected" : ""
                              }`}
                              onClick={() => {
                                setValue("city", cityName, {
                                  shouldValidate: false,
                                });
                                setIsAreaOpen(false);
                              }}
                            >
                              {cityName}
                            </div>
                          ))
                        ) : (
                          <div
                            className="custom-dropdown-option"
                            style={{ color: "#9ca3af" }}
                          >
                            No cities available
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
                <input type="hidden" {...register("city")} />
              </div>
            </>
          ) : (
            // UAE: Emirate
            <div
              className="school-form-inner-box"
              ref={formRefs.workState}
              style={{ flex: "1 1 70%", minWidth: "0", maxWidth: "100%" }}
            >
              <Typography
                variant="body1"
                component="p"
                sx={{ fontSize: "14px", fontWeight: "bold", mb: 1 }}
              >
                Emirate
                <span style={{ color: "red" }}>*</span>
              </Typography>
              <div
                className="custom-dropdown-container"
                ref={emirateDropdownRef}
              >
                <div
                  className={`custom-dropdown ${
                    isEmirateOpen ? "active" : ""
                  } ${!!formState.errors.workState ? "error" : ""}`}
                  onClick={() => setIsEmirateOpen(!isEmirateOpen)}
                  style={{ minWidth: "auto" }}
                >
                  <span
                    className={`custom-dropdown-text ${
                      !watch("workState") ? "custom-dropdown-placeholder" : ""
                    }`}
                  >
                    {watch("workState") || "Select Emirate"}
                  </span>
                  <svg
                    className={`custom-dropdown-arrow ${
                      isEmirateOpen ? "open" : ""
                    }`}
                    width="20"
                    height="20"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                  >
                    <path
                      fillRule="evenodd"
                      d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                      clipRule="evenodd"
                    />
                  </svg>
                </div>

                {isEmirateOpen && (
                  <div className="custom-dropdown-menu">
                    <div className="custom-dropdown-options">
                      {states?.map((state, idx) => (
                        <div
                          key={idx}
                          className={`custom-dropdown-option ${
                            watch("workState") === state.stateName
                              ? "selected"
                              : ""
                          }`}
                          onClick={() => {
                            setValue("workState", state.stateName, {
                              shouldValidate: true,
                            });
                            setIsEmirateOpen(false);
                          }}
                        >
                          {state.stateName}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
              {!!formState.errors.workState && (
                <span className="errorMessage">
                  {formState.errors.workState.message}
                </span>
              )}
            </div>
          )}
        </div>
        <div className="school-img-box">
          <div className="school-form-inner-box">
            <Typography
              variant="body1"
              component="p"
              sx={{ fontSize: "14px", fontWeight: "bold", mb: 1 }}
            >
              Years of Experience
              <span style={{ color: "red" }}>*</span>
            </Typography>
            <div className="experience_holder">
              <TextField
                placeholder="Min Exp(years)"
                {...register("minExp")}
                error={!!formState.errors.minExp}
                helperText={formState.errors.minExp?.message}
                inputProps={{
                  min: 0,
                  step: 1,
                }}
                onKeyDown={(e) => {
                  if (
                    e.key === "e" ||
                    e.key === "E" ||
                    e.key === "-" ||
                    e.key === "+"
                  ) {
                    e.preventDefault();
                  }
                }}
                onChange={(e) => {
                  const value = Math.max(0, parseInt(e.target.value) || 0);
                  setValue("minExp", value, { shouldValidate: true });
                }}
                size="small"
                type="number"
              />{" "}
              <TextField
                placeholder="Max Exp(years)"
                {...register("maxExp")}
                error={!!formState.errors.maxExp}
                helperText={formState.errors.maxExp?.message}
                inputProps={{
                  min: 0,
                  step: 1,
                }}
                onKeyDown={(e) => {
                  if (
                    e.key === "e" ||
                    e.key === "E" ||
                    e.key === "-" ||
                    e.key === "+"
                  ) {
                    e.preventDefault();
                  }
                }}
                onChange={(e) => {
                  const value = Math.max(0, parseInt(e.target.value) || 0);
                  setValue("maxExp", value, { shouldValidate: true });
                }}
                size="small"
                type="number"
              />
            </div>
          </div>
          <div className="school-form-inner-box">
            <Typography
              variant="body1"
              component="p"
              sx={{ fontSize: "14px", fontWeight: "bold", mb: 1 }}
            >
              Job Type
              <span style={{ color: "red" }}>*</span>
            </Typography>
            <div className="custom-dropdown-container" ref={jobTypeDropdownRef}>
              <div
                className={`custom-dropdown ${isJobTypeOpen ? "active" : ""} ${
                  !!formState.errors.jobType ? "error" : ""
                }`}
                onClick={() => setIsJobTypeOpen(!isJobTypeOpen)}
              >
                <span
                  className={`custom-dropdown-text ${
                    !watch("jobType") ? "custom-dropdown-placeholder" : ""
                  }`}
                >
                  {watch("jobType") || "Select Job Type"}
                </span>
                <svg
                  className={`custom-dropdown-arrow ${
                    isJobTypeOpen ? "open" : ""
                  }`}
                  width="20"
                  height="20"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                >
                  <path
                    fillRule="evenodd"
                    d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                    clipRule="evenodd"
                  />
                </svg>
              </div>

              {isJobTypeOpen && (
                <div className="custom-dropdown-menu">
                  <div className="custom-dropdown-options">
                    {workTypes?.map((workType: any, idx: number) => (
                      <div
                        key={workType.workTypeId ?? idx}
                        className={`custom-dropdown-option ${
                          String(watch("jobType") || "")
                            .toLowerCase()
                            .replace(/[\s-]/g, "") ===
                          String(workType.workTypeName || "")
                            .toLowerCase()
                            .replace(/[\s-]/g, "")
                            ? "selected"
                            : ""
                        }`}
                        onClick={() => {
                          setValue("jobType", workType.workTypeName, {
                            shouldValidate: true,
                          });
                          setValue("workTypeId", workType._id, {
                            shouldValidate: false,
                          });
                          setIsJobTypeOpen(false);
                        }}
                      >
                        {workType.workTypeName}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
            {!!formState.errors.jobType && (
              <span className="errorMessage">
                {formState.errors.jobType.message}
              </span>
            )}
          </div>
        </div>
        <div className="school-img-box">
          <div className="school-form-inner-box">
            <Typography
              variant="body1"
              component="p"
              sx={{ fontSize: "14px", fontWeight: "bold", mb: 1 }}
            >
              Preferred Industry{" "}
              <span className="secondaryGrayText"> (optional)</span>
            </Typography>

            <Box
              sx={{
                width: "100%",
              }}
            >
              <div
                className="custom-dropdown-container"
                ref={industryDropdownRef}
              >
                <div
                  className={`custom-dropdown ${
                    isIndustryOpen ? "active" : ""
                  } ${!!formState.errors.industryName ? "error" : ""}`}
                  onClick={() => setIsIndustryOpen(!isIndustryOpen)}
                >
                  <span
                    className={`custom-dropdown-text ${
                      !watch("industryName")
                        ? "custom-dropdown-placeholder"
                        : ""
                    }`}
                  >
                    {watch("industryName") || "Select Preferred Industry"}
                  </span>
                  <svg
                    className={`custom-dropdown-arrow ${
                      isIndustryOpen ? "open" : ""
                    }`}
                    width="20"
                    height="20"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                  >
                    <path
                      fillRule="evenodd"
                      d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                      clipRule="evenodd"
                    />
                  </svg>
                </div>

                {isIndustryOpen && (
                  <div className="custom-dropdown-menu">
                    <div className="custom-dropdown-options">
                      {industries?.map((industry, idx) => (
                        <div
                          key={idx}
                          className={`custom-dropdown-option ${
                            watch("industryName") === industry.industryName
                              ? "selected"
                              : ""
                          }`}
                          onClick={() => {
                            setValue("industryName", industry.industryName, {
                              shouldValidate: true,
                            });
                            setValue("industryId", industry._id);
                            setIsIndustryOpen(false);
                          }}
                        >
                          {industry.industryName}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
              {!!formState.errors.industryName && (
                <span className="errorMessage">
                  {formState.errors.industryName.message}
                </span>
              )}
            </Box>
          </div>
          <div className="school-form-inner-box">
            <Typography
              variant="body1"
              component="p"
              sx={{ fontSize: "14px", fontWeight: "bold", mb: 1 }}
            >
              Work Place Type
              <span style={{ color: "red" }}>*</span>
            </Typography>

            <div
              className="custom-dropdown-container"
              ref={workTypeDropdownRef}
            >
              <div
                className={`custom-dropdown ${isWorkTypeOpen ? "active" : ""} ${
                  !!formState.errors.workType ? "error" : ""
                }`}
                onClick={() => setIsWorkTypeOpen(!isWorkTypeOpen)}
              >
                <span
                  className={`custom-dropdown-text ${
                    !watch("workType") ? "custom-dropdown-placeholder" : ""
                  }`}
                >
                  {watch("workType") || "Select Work place type"}
                </span>
                <svg
                  className={`custom-dropdown-arrow ${
                    isWorkTypeOpen ? "open" : ""
                  }`}
                  width="20"
                  height="20"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                >
                  <path
                    fillRule="evenodd"
                    d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                    clipRule="evenodd"
                  />
                </svg>
              </div>

              {isWorkTypeOpen && (
                <div className="custom-dropdown-menu">
                  <div className="custom-dropdown-options">
                    {workPlaces?.map((workPlace, idx) => (
                      <div
                        key={workPlace.workplaceId ?? idx}
                        className={`custom-dropdown-option ${
                          String(watch("workType") || "")
                            .toLowerCase()
                            .replace(/[\s-]/g, "") ===
                          String(workPlace.workplaceName || "")
                            .toLowerCase()
                            .replace(/[\s-]/g, "")
                            ? "selected"
                            : ""
                        }`}
                        onClick={() => {
                          setValue("workType", workPlace.workplaceName, {
                            shouldValidate: true,
                          });
                          setValue("workplaceTypeId" as any, workPlace._id, {
                            shouldValidate: false,
                          });
                          setIsWorkTypeOpen(false);
                        }}
                      >
                        {workPlace.workplaceName}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
            {!!formState.errors.workType && (
              <span className="errorMessage">
                {formState.errors.workType.message}
              </span>
            )}
          </div>
        </div>
        <div className="school-img-box" style={{ marginBottom: "10px" }}>
          <div className="school-form-inner-box">
            <Typography
              variant="body1"
              component="p"
              sx={{ fontSize: "14px", fontWeight: "bold", mb: 1 }}
            >
              No.of vacancies
              <span className="secondaryGrayText"> (optional)</span>
            </Typography>
            <TextField
              placeholder="No.of Vacancies"
              {...register("vacancy")}
              error={!!formState.errors.vacancy}
              helperText={formState.errors.vacancy?.message}
              size="small"
              type="number"
              inputProps={{
                min: 0,
                step: 1,
              }}
              onKeyDown={(e) => {
                if (
                  e.key === "e" ||
                  e.key === "E" ||
                  e.key === "-" ||
                  e.key === "+"
                ) {
                  e.preventDefault();
                }
              }}
              onChange={(e) => {
                const value = Math.max(0, parseInt(e.target.value) || 0);
                setValue("vacancy", value, { shouldValidate: true });
              }}
            />
          </div>
          <div className="school-form-inner-box">
            <Typography
              variant="body1"
              component="p"
              sx={{ fontSize: "14px", fontWeight: "bold", mb: 1 }}
            >
              Education
              <span style={{ color: "red" }}>*</span>
            </Typography>

            <div
              className="custom-dropdown-container"
              ref={educationDropdownRef}
            >
              <div
                className={`custom-dropdown ${
                  isEducationOpen ? "active" : ""
                } ${!!formState.errors.qualTypeName ? "error" : ""}`}
                onClick={() => setIsEducationOpen(!isEducationOpen)}
              >
                <span
                  className={`custom-dropdown-text ${
                    !watch("qualTypeName") ? "custom-dropdown-placeholder" : ""
                  }`}
                >
                  {selectedQualification?.qualTypeName ||
                    watch("qualTypeName") ||
                    "Select Education Level"}
                </span>
                <svg
                  className={`custom-dropdown-arrow ${
                    isEducationOpen ? "open" : ""
                  }`}
                  width="20"
                  height="20"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                >
                  <path
                    fillRule="evenodd"
                    d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                    clipRule="evenodd"
                  />
                </svg>
              </div>

              {isEducationOpen && (
                <div className="custom-dropdown-menu">
                  <div className="custom-dropdown-options">
                    {qualificationTypes?.map((qualification, idx) => (
                      <div
                        key={qualification._id ?? idx}
                        className={`custom-dropdown-option ${
                          selectedQualification?._id === qualification._id
                            ? "selected"
                            : ""
                        }`}
                        onClick={() => {
                          setValue("qualTypeName", qualification.qualTypeName, {
                            shouldValidate: true,
                          });
                          setValue("qualTypeId", qualification._id, {
                            shouldValidate: false,
                          });
                          setIsEducationOpen(false);
                        }}
                      >
                        {qualification.qualTypeName}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
            {!!formState.errors.qualTypeName && (
              <span className="errorMessage">
                {formState.errors.qualTypeName.message}
              </span>
            )}
          </div>
        </div>
        <Divider />
        <h3 style={{ marginTop: "16px", marginBottom: "8px" }}>
          Job Requirements
        </h3>
        <div className="school-img-box">
          <div className="school-form-inner-box" ref={formRefs.jobDesc}>
            <Typography
              variant="body1"
              component="p"
              sx={{ fontSize: "14px", fontWeight: "bold", mb: 1 }}
            >
              Job Description
              <span style={{ color: "red" }}>*</span>
            </Typography>
            {/* <TextField
              placeholder="Type here..."
              {...register("jobDesc")}
              error={!!formState.errors.jobDesc}
              helperText={formState.errors.jobDesc?.message}
              size="small"
              multiline
              type="textarea"
              rows={7}
              className="profile_desc_text"
            /> */}
            <RichText
              value={jobDesc}
              setValue={handleJobDesc}
              error={formState.errors.jobDesc?.message || ""}
            />
            {formState.errors.jobDesc && (
              <span className="errorMessage">
                {formState.errors.jobDesc.message}
              </span>
            )}
          </div>
        </div>
        <div className="school-img-box">
          <div className="school-form-inner-box">
            <Typography
              variant="body1"
              component="p"
              sx={{ fontSize: "14px", fontWeight: "bold", mb: 1 }}
            >
              Job Requirements
              <span className="secondaryGrayText"> (optional)</span>
            </Typography>
            {/* <TextField
              placeholder="Type here..."
              {...register("jobReq")}
              error={!!formState.errors.jobReq}
              helperText={formState.errors.jobReq?.message}
              size="small"
              multiline
              type="textarea"
              rows={7}
              className="profile_desc_text"
            /> */}
            <RichText value={jobReq} setValue={setJobReq} />
          </div>
        </div>
        <div className="school-img-box">
          <div className="school-form-inner-box" ref={formRefs.skills}>
            <Typography
              variant="body1"
              component="p"
              sx={{ fontSize: "14px", fontWeight: "bold", mb: 1 }}
            >
              Skills
              <span style={{ color: "red" }}>*</span>
            </Typography>
            <AutocompleteBox
              passedSkills={skills || []}
              setPassedSkills={handleSkillChange}
              error={formState.errors.skills?.message || ""}
            />
            {formState.errors.skills && (
              <span className="errorMessage">
                {formState.errors.skills.message}
              </span>
            )}
          </div>
        </div>
        <br />
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            gap: "12px",
          }}
        >
          <Button
            variant="outlined"
            onClick={() => handleDone()}
            disabled={loading}
            sx={{
              borderColor: "#d1d5db",
              backgroundColor: "#f3f4f6",
              color: "#6b7280",
              textTransform: "none",
              fontWeight: "600",
              padding: "10px 24px",
              borderRadius: "8px",
              "&:hover": {
                backgroundColor: "#e5e7eb",
              },
            }}
          >
            Cancel
          </Button>
          <Button
            disabled={loading}
            className="createJobBtn_ak"
            type="submit"
            variant="contained"
            sx={{
              backgroundColor: "#10b981",
              color: "white",
              textTransform: "none",
              fontWeight: "600",
              padding: "10px 24px",
              borderRadius: "8px",
              "&:hover": {
                backgroundColor: "#059669",
              },
            }}
          >
            {type === "edit" ? "Save Changes" : "Create job"}
          </Button>
        </div>
      </form>
    </>
  );
};

export default JobForm;
